import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, InputBase, styled, useMediaQuery, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  SEARCH_ARIA_LABEL,
  SEARCH_PLACEHOLDER_DESKTOP,
  SEARCH_PLACEHOLDER_MOBILE,
} from "../../pages/shared/constants";

interface SearchComponentProps {
  inputRef?: React.Ref<HTMLInputElement>;
  value?: string;
  onChange?: (value: string) => void;
  /** Fired on Enter / the keyboard's search key. */
  onSubmit?: () => void;
}

// A real `<form>` so that pressing Enter — or the "search" key on a phone keyboard — is a submit
// event we can act on. A bare input swallows Enter, which is why the keyboard used to stay open on
// top of the results.
const SearchBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1, 1.25),
  width: "100%",
  minWidth: 0,
}));

/** Below this, iOS Safari zooms the page when the field takes focus. */
const IOS_NO_ZOOM_FONT_SIZE = 16;

const StyledInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  color: theme.palette.text.primary,
  fontSize: theme.typography.body2.fontSize,
  // The placeholder is left to the theme (EB-554), which fades it just enough to read as a hint
  // and no further — a local 0.6 put the one instruction on the storefront at ~3.6:1.
  /**
   * iOS Safari zooms into any focused input rendered below 16px, and the zoom leaves the page
   * scrolled sideways — the shopper starts typing and the storefront appears to jump away from
   * them. 16px is the whole fix.
   *
   * Keyed on `pointer: coarse` rather than a width breakpoint because the zoom is a property of
   * the input method, not the viewport: an iPhone in landscape is wider than `sm` and still zooms.
   * Pointer-precise devices keep 14px, which is what lets the long desktop placeholder fit.
   */
  "@media (pointer: coarse)": {
    "& .MuiInputBase-input": {
      fontSize: IOS_NO_ZOOM_FONT_SIZE,
    },
  },
}));

// Decoration, not a control: the field filters on every keystroke, so a clickable magnifier would
// be a button with nothing to do.
const SearchGlyph = styled(SearchIcon)(({ theme }) => ({
  color: theme.palette.text.primary,
  opacity: 0.7,
  fontSize: 20,
  flexShrink: 0,
}));

/** Width of `text` as this input would actually render it, or `0` when it cannot be measured. */
const measureText = (input: HTMLInputElement, text: string): number => {
  const context = document.createElement("canvas").getContext("2d");
  if (!context) return 0;
  const style = window.getComputedStyle(input);
  context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return context.measureText(text).width;
};

/**
 * Which of the two placeholders to show.
 *
 * Mobile always gets the short, conversational one — that is the design intent, not a space
 * calculation, and the full-width mobile field would happily fit the long string.
 *
 * Desktop wants the long one, but only when it actually fits. The check is against the **field**
 * rather than the viewport because the storefront's content column is 50% of the window at `lg`
 * and 70% at `md`, which makes the field *narrower* on a 1280px screen than on a 1000px one. No
 * breakpoint can express that, and choosing by viewport alone truncated the placeholder mid-word
 * at the most common desktop size.
 */
const useFittingPlaceholder = (
  inputRef: React.MutableRefObject<HTMLInputElement | null>,
  isDesktop: boolean,
) => {
  const [longFits, setLongFits] = useState(true);

  const measure = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    const available = input.getBoundingClientRect().width;
    // jsdom and pre-layout paints report 0; treating that as "too narrow" would flash the short
    // copy on every mount.
    if (available === 0) return;
    const needed = measureText(input, SEARCH_PLACEHOLDER_DESKTOP);
    setLongFits(needed === 0 || needed <= available);
  }, [inputRef]);

  useEffect(() => {
    measure();

    // The webfont lands after first paint, and Inter is wider than the fallback — without this the
    // decision is made against metrics the shopper never sees.
    document.fonts?.ready.then(measure).catch(() => undefined);

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    if (inputRef.current) observer.observe(inputRef.current);
    return () => observer.disconnect();
  }, [measure, inputRef]);

  return isDesktop && longFits ? SEARCH_PLACEHOLDER_DESKTOP : SEARCH_PLACEHOLDER_MOBILE;
};

const SearchComponent: React.FC<SearchComponentProps> = ({
  inputRef,
  value,
  onChange,
  onSubmit,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const innerRef = useRef<HTMLInputElement | null>(null);
  const placeholder = useFittingPlaceholder(innerRef, isDesktop);

  // The caller may want the node too (focus management), so both refs get it.
  const setRefs = useCallback(
    (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof inputRef === "function") inputRef(node);
      else if (inputRef)
        (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
    },
    [inputRef],
  );

  return (
    <SearchBar
      component="form"
      role="search"
      // Nothing is posted anywhere — the term goes into the URL. `preventDefault` is what stops the
      // browser from reloading the page on Enter.
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        onSubmit?.();
      }}
    >
      <SearchGlyph aria-hidden="true" />
      <StyledInput
        placeholder={placeholder}
        inputProps={{
          "aria-label": SEARCH_ARIA_LABEL,
          // Labels the phone keyboard's action key "search" instead of "go", and on iOS it is what
          // makes that key submit a single-input form.
          enterKeyHint: "search",
        }}
        inputRef={setRefs}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </SearchBar>
  );
};

export default SearchComponent;
